import React, { useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { SocketProvider, useSocket } from './context/SocketContext';
import SplashPage from './pages/SplashPage';
import LoginPage from './pages/LoginPage';
import LobbyPage from './pages/LobbyPage';
import GamePage from './pages/GamePage';
import SettingsPage from './pages/SettingsPage';

/**
 * Centralized state-driven navigation controller.
 * All navigation decisions based on backend state happen here.
 * Frontend finds its player by ID from localStorage, then checks player properties.
 */
const StateRouter: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameState } = useSocket();
  const wasOnGameRef = useRef<boolean>(false);
  const lastKnownLevelRef = useRef<number>(1);

  useEffect(() => {
    if (!gameState) return;

    const currentPath = location.pathname;
    const playerId = localStorage.getItem('playerId');

    // Find my player from state
    const myPlayer = playerId
      ? gameState.players?.find(p => p.player_id === playerId)
      : null;

    // Track player's current level while they're in the game
    if (myPlayer) {
      lastKnownLevelRef.current = myPlayer.current_level;
    }

    // Debug log
    console.log('[StateRouter] State update:', {
      path: currentPath,
      status: gameState.status,
      playerId,
      myPlayer: myPlayer ? { status: myPlayer.status, current_level: myPlayer.current_level } : 'not found',
    });

    // Track if we were on game page
    if (currentPath === '/game') {
      wasOnGameRef.current = true;
    }

    // If on game page
    if (currentPath === '/game') {
      // If game status is waiting → game ended/reset, go to lobby (normal end)
      if (gameState.status === 'waiting') {
        console.log('[StateRouter] Game ended, navigating to lobby');
        wasOnGameRef.current = false;
        navigate('/lobby', { replace: true });
        return;
      }

      // If game is playing but player not in list → they left mid-game, show challenge failed
      if (gameState.status === 'playing' && !myPlayer && wasOnGameRef.current) {
        console.log('[StateRouter] Player left mid-game, navigating to lobby with challenge failed');
        const failedLevel = lastKnownLevelRef.current;
        wasOnGameRef.current = false;
        lastKnownLevelRef.current = 1; // Reset for next game
        navigate('/lobby', {
          state: { challengeFailed: true, failedLevel },
          replace: true
        });
        return;
      }
    }

    // If on lobby and game starts (and player is actively playing), go to game
    if (currentPath === '/lobby') {
      if (gameState.status === 'playing' && myPlayer?.status === 'playing') {
        console.log('[StateRouter] Game started, navigating to game');
        navigate('/game', { replace: true });
      }
    }

  }, [gameState, navigate, location.pathname]);

  return <>{children}</>;
};

function App() {
  return (
    <SocketProvider>
      <BrowserRouter>
        <StateRouter>
          <Routes>
            <Route path="/" element={<SplashPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/lobby" element={<LobbyPage />} />
            <Route path="/game" element={<GamePage />} />
            <Route path="/settings" element={<SettingsPage />} />
          </Routes>
        </StateRouter>
      </BrowserRouter>
    </SocketProvider>
  );
}

export default App;
